import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  CheckCircle2,
  Upload,
  Trash2,
  FileText,
  Image,
  FileSpreadsheet,
  Film,
  Music,
  Lock,
  Globe,
  Pencil,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';

import PageHeader from '../../components/layout/PageHeader';
import FileUploadZone from '../../components/features/documents/FileUploadZone';
import HashtagInput from '../../components/features/documents/HashtagInput';
import SearchableSelect from '../../components/ui/SearchableSelect';
import HierarchyNodePicker from '../../components/hierarchy/HierarchyNodePicker';
import hierarchyService from '../../services/hierarchy.service';
import caseService from '../../services/case.service';
import documentService from '../../services/document.service';
import approvalService from '../../services/approval.service';
import draftService from '../../services/draft.service';
import contentManagementService from '../../services/contentManagement.service';
import { useAuthStore } from '../../store/authStore';
import type { FileWithMeta } from '../../types/document.types';
import type { RrmsCase } from '../../types/case.types';
import { ROUTES } from '../../config/routes.config';
import { formatDocumentDisplayName } from '../../utils/documentName';

// Draft autosave cadence. Each tick saves only when something changed since
// the last successful save, so the interval can stay short without spamming
// the server.
const DRAFT_AUTOSAVE_INTERVAL_MS = 30_000;

function genId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Upload-form redesign: records are filed by hierarchy (picker) + the fields
// below. The old CID chain (state/district/PS/case type/status/…) is gone;
// fir_number now carries the "File Number" (column reuse, label change).
const caseRegisterSchema = z
  .object({
    year: z
      .number()
      .min(1940, 'Year must be 1940 or later')
      .max(new Date().getFullYear(), 'Year cannot be in the future'),
    fir_number: z.string().min(1, 'File number is required').max(100),
    // Optional: only shown/enforced when the admin has defined a category master.
    category_node_id: z.string().optional(),
    subject: z.string().min(1, 'Subject is required').max(500),
    // Required: the File Start Date drives the storage path
    // (Section/…/Category/Year/Date) and the Content Management tree.
    file_start_date: z
      .string()
      .min(1, 'File start date is required')
      .refine(
        (value) => /^\d{4}-\d{2}-\d{2}$/.test(value),
        'File start date must be in YYYY-MM-DD format'
      ),
    file_end_date: z
      .string()
      .refine(
        (value) => value.trim() === '' || /^\d{4}-\d{2}-\d{2}$/.test(value),
        'File end date must be in YYYY-MM-DD format'
      ),
  })
  .refine(
    (v) => !v.file_start_date || !v.file_end_date || v.file_end_date >= v.file_start_date,
    { message: 'File end date cannot be before the start date', path: ['file_end_date'] }
  );

type CaseRegisterForm = z.infer<typeof caseRegisterSchema>;

function getFirstCaseFormErrorMessage(
  errors: Record<string, any>
): string | null {
  const orderedFields = [
    'year',
    'fir_number',
    'category_node_id',
    'subject',
    'file_start_date',
    'file_end_date',
  ];
  for (const key of orderedFields) {
    const msg = errors?.[key]?.message;
    if (typeof msg === 'string' && msg.trim().length > 0) return msg;
  }
  return null;
}

function getApiValidationMessage(
  err: any,
  fallback: string,
  context?: { rawFirInput?: string }
): string {
  const data = err?.response?.data;
  const errors = Array.isArray(data?.errors) ? data.errors : [];
  if (errors.length > 0) {
    // Prefer FIR-specific message when available, since this is the most common
    // validation failure in this form flow.
    const firErr = errors.find(
      (e: any) => e?.path === 'fir_number' && typeof e?.msg === 'string'
    );
    if (firErr?.msg) {
      const hasRequiredFirError = errors.some(
        (e: any) => e?.path === 'fir_number' && e?.msg === 'fir_number is required'
      );
      const hasDigitFirError = errors.some(
        (e: any) =>
          e?.path === 'fir_number' && e?.msg === 'fir_number must be 1 to 4 digits'
      );
      const rawFir = String(context?.rawFirInput || '').trim();
      // If user typed something non-empty (e.g. ABCD), prefer digits-format error.
      if (hasRequiredFirError && hasDigitFirError && rawFir.length > 0) {
        return 'FIR number must be 1 to 4 digits.';
      }
      if (firErr.msg.includes('required')) return 'FIR number is required.';
      if (firErr.msg.includes('1 to 4 digits'))
        return 'FIR number must be 1 to 4 digits.';
      return firErr.msg;
    }
    const firstMsg = errors.find((e: any) => typeof e?.msg === 'string')?.msg;
    if (firstMsg) return firstMsg;
  }
  return data?.message || fallback;
}

function getFileIcon(file: File) {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff'].includes(ext))
    return <Image size={15} style={{ color: 'var(--color-primary)' }} />;
  if (['xls', 'xlsx'].includes(ext))
    return <FileSpreadsheet size={15} style={{ color: '#22a569' }} />;
  if (['mp4', 'webm', 'mov', 'avi', 'wmv'].includes(ext))
    return <Film size={15} style={{ color: '#8b5cf6' }} />;
  if (['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(ext))
    return <Music size={15} style={{ color: '#f59e0b' }} />;
  return <FileText size={15} style={{ color: 'var(--color-gray-500)' }} />;
}

function getFileIconForExtension(ext?: string | null, size = 36) {
  const lower = (ext || '').toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff'].includes(lower)) {
    return <Image size={size} style={{ color: 'var(--color-primary)' }} />;
  }
  if (['xls', 'xlsx'].includes(lower)) {
    return <FileSpreadsheet size={size} style={{ color: '#22a569' }} />;
  }
  if (['mp4', 'webm', 'mov', 'avi', 'wmv'].includes(lower)) {
    return <Film size={size} style={{ color: '#8b5cf6' }} />;
  }
  if (['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(lower)) {
    return <Music size={size} style={{ color: '#f59e0b' }} />;
  }
  return <FileText size={size} style={{ color: 'var(--color-gray-600)' }} />;
}

function normaliseHashtagArray(raw: any): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((t) => (typeof t === 'string' ? t.trim() : String(t)))
      .filter(Boolean);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    // Try JSON array first: '["tag1","tag2"]'
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return normaliseHashtagArray(parsed);
        }
      } catch {
        // fall through to comma split
      }
    }
    return trimmed
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

type ExistingDraftDocument = {
  rrms_document_draft_id: string;
  original_file_name?: string | null;
  file_size_bytes?: number | string | null;
  stored_file_path?: string | null;
  folder_type?: string | null;
  document_type?: string | null;
  classification?: 'Public' | 'Confidential' | string | null;
  hashtags?: string[] | null;
};

type ExistingCaseDocument = {
  document_id: string;
  original_file_name: string;
  file_size_bytes?: number | string | null;
  file_extension?: string | null;
  folder_type: string;
  document_type: string;
  classification: 'Public' | 'Confidential' | string;
  hashtags?: string[] | null;
  uploaded_by_user_id: number;
  approval_status: string;
};

function formatFileSize(bytesLike?: number | string | null): string {
  const bytes = Number(bytesLike || 0);
  if (!bytes || Number.isNaN(bytes)) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function normalizeDraftDocuments(
  draftDocuments: any[]
): ExistingDraftDocument[] {
  return draftDocuments
    .filter((d: any) => Boolean(d.original_file_name) || Boolean(d.stored_file_path))
    .map((d: any) => ({
      rrms_document_draft_id: d.rrms_document_draft_id,
      original_file_name: d.original_file_name,
      file_size_bytes: d.file_size_bytes,
      stored_file_path: d.stored_file_path,
      folder_type: d.folder_type ?? '',
      document_type: d.document_type ?? '',
      classification: d.classification ?? 'Public',
      hashtags: Array.isArray(d.hashtags) ? d.hashtags : [],
    }));
}

const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { can } = useAuthStore();
  const authUserId = useAuthStore((s) => s.user?.user_id ?? null);
  const [selectedCase, setSelectedCase] = useState<RrmsCase | null>(null);
  // Placement in the admin-defined hierarchy (dynamic; levels come from the master).
  const [hierarchyNodeId, setHierarchyNodeId] = useState<number | null>(null);
  const [hierarchyPath, setHierarchyPath] = useState<number[]>([]);
  const [hierarchyAvailable, setHierarchyAvailable] = useState(false);
  const [caseSearchQuery, setCaseSearchQuery] = useState('');
  const [caseSearchResults, setCaseSearchResults] = useState<RrmsCase[]>([]);
  const [isSearchingCase, setIsSearchingCase] = useState(false);

  // Per-file upload state
  const [filesWithMeta, setFilesWithMeta] = useState<FileWithMeta[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedExistingIds, setSelectedExistingIds] = useState<Set<string>>(new Set());
  // Per-file metadata is now classification + hashtags only (folder/document
  // types were dropped with the upload-form redesign — grouping comes from the
  // hierarchy + File Category instead).
  const [bulkValues, setBulkValues] = useState({
    classification: 'Public' as 'Public' | 'Confidential',
    hashtags: [] as string[],
  });
  const [existingBulkValues, setExistingBulkValues] = useState({
    classification: 'Public' as 'Public' | 'Confidential',
    hashtags: [] as string[],
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [existingDraftFileCount, setExistingDraftFileCount] = useState(0);
  const [existingDraftDocuments, setExistingDraftDocuments] = useState<ExistingDraftDocument[]>([]);
  const [removingDraftDocumentId, setRemovingDraftDocumentId] = useState<string | null>(null);
  const [existingCaseDocuments, setExistingCaseDocuments] = useState<ExistingCaseDocument[]>([]);
  const [isLoadingExistingCaseDocs, setIsLoadingExistingCaseDocs] = useState(false);
  const [editingCaseDocumentId, setEditingCaseDocumentId] = useState<string | null>(null);
  const [editingCaseDocHashtags, setEditingCaseDocHashtags] = useState<string[]>([]);
  const [editingCaseDocClassification, setEditingCaseDocClassification] = useState<
    'Public' | 'Confidential'
  >('Public');

  // ---- Draft autosave ------------------------------------------------------
  // Saves the draft automatically on an interval once at least one meaningful
  // input exists. The guards below make a tick unable to create a second
  // draft or re-append files that an earlier save already attached.
  const [lastDraftSavedAt, setLastDraftSavedAt] = useState<Date | null>(null);
  const filesWithMetaRef = useRef<FileWithMeta[]>(filesWithMeta);
  const existingDraftDocumentsRef = useRef<ExistingDraftDocument[]>(existingDraftDocuments);
  const hierarchyNodeIdRef = useRef<number | null>(hierarchyNodeId);
  const bulkValuesRef = useRef(bulkValues);
  const currentDraftIdRef = useRef<string | null>(null);
  const draftSaveInFlightRef = useRef(false);
  const draftSavePromiseRef = useRef<Promise<boolean> | null>(null);
  const isSubmittingRef = useRef(false);
  // Set when the draft turns out to be deleted elsewhere (404): autosave must
  // not recreate a draft the user chose to delete.
  const autosavePausedRef = useRef(false);
  // Change counter: bumped on every draft-relevant edit. A save records the
  // counter value it captured, so edits made while the request is in flight
  // are picked up by the next tick instead of being lost or double-saved.
  const dirtySeqRef = useRef(0);
  const savedSeqRef = useRef(0);
  const autoSaveTickRef = useRef<() => void>(() => {});
  filesWithMetaRef.current = filesWithMeta;
  existingDraftDocumentsRef.current = existingDraftDocuments;
  hierarchyNodeIdRef.current = hierarchyNodeId;
  bulkValuesRef.current = bulkValues;

  const markDraftDirty = () => {
    dirtySeqRef.current += 1;
  };

  useEffect(() => {
    document.title = 'RRMS | Upload Documents';
  }, []);

  useEffect(() => {
    const prefill = (location.state ||
      null) as {
      prefillCaseId?: string | number;
      prefillCmsNumber?: string;
    } | null;
    if (!prefill?.prefillCaseId) return;

    let cancelled = false;
    const applySelectedCase = (rawCase: any) => {
      if (cancelled || !rawCase) return;
      setSelectedCase({
        case_id: String(rawCase.case_id ?? prefill.prefillCaseId),
        rrms_ref_number: String(rawCase.rrms_ref_number ?? ''),
        state: rawCase.state ?? 'Karnataka',
        cms_number: rawCase.cms_number ?? prefill.prefillCmsNumber ?? '',
        district: rawCase.district ?? '',
        ps_name: rawCase.ps_name ?? '',
        fir_number: rawCase.fir_number ?? '',
        fir_registration_date: rawCase.fir_registration_date ?? null,
        case_type: rawCase.case_type ?? '',
        case_type_code: rawCase.case_type_code ?? '',
        year: Number(rawCase.year ?? new Date().getFullYear()),
        case_status: rawCase.case_status ?? '',
        cms_case_name: rawCase.cms_case_name ?? '',
        final_report_status: rawCase.final_report_status ?? null,
        office_of: rawCase.office_of ?? null,
        letter_no: rawCase.letter_no ?? null,
        addressed_to: rawCase.addressed_to ?? null,
        author: rawCase.author ?? null,
        uploaded_by_user_id: Number(rawCase.uploaded_by_user_id ?? 0),
        division_id: Number(rawCase.division_id ?? 0),
        department_id: Number(rawCase.department_id ?? 0),
        created_at: rawCase.created_at ?? new Date().toISOString(),
      });
    };

    (async () => {
      try {
        const res = await caseService.getCaseById(String(prefill.prefillCaseId));
        const data = (res as any)?.data?.data ?? (res as any)?.data;
        const fetchedCase = data?.case ?? data;
        if (fetchedCase) {
          applySelectedCase(fetchedCase);
          return;
        }
      } catch {
        // Ignore and apply minimal fallback payload below.
      }

      applySelectedCase({
        case_id: prefill.prefillCaseId,
        cms_number: prefill.prefillCmsNumber ?? '',
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [location.state]);

  // Load existing case documents when Upload More is used (prefilled case)
  const reloadExistingCaseDocs = useCallback(async () => {
    if (!selectedCase || !authUserId) {
      setExistingCaseDocuments([]);
      return;
    }
    setIsLoadingExistingCaseDocs(true);
    try {
      const res = await contentManagementService.getCaseDocuments(
        String(selectedCase.case_id),
        { page: 1, limit: 100 }
      );
      const payload: any = (res as any).data?.data ?? (res as any).data;
      const items: any[] = payload?.items ?? payload?.documents ?? payload ?? [];
      const docs: ExistingCaseDocument[] = items.map((row: any) => ({
        document_id: String(row.document_id ?? ''),
        original_file_name:
          row.original_file_name ?? row.file_name ?? 'Untitled document',
        file_size_bytes: row.file_size_bytes,
        file_extension: row.file_extension ?? null,
        folder_type: row.folder_type ?? 'Case Files',
        document_type: row.document_type ?? 'Document',
        classification: row.classification ?? 'Public',
        hashtags: normaliseHashtagArray(row.hashtags),
        uploaded_by_user_id: Number(row.uploaded_by_user_id ?? 0),
        approval_status: row.approval_status ?? 'Pending',
      }));
      setExistingCaseDocuments(docs);
    } catch {
      setExistingCaseDocuments([]);
    } finally {
      setIsLoadingExistingCaseDocs(false);
    }
  }, [selectedCase, authUserId]);

  useEffect(() => {
    reloadExistingCaseDocs();
  }, [reloadExistingCaseDocs]);

  const caseForm = useForm<CaseRegisterForm>({
    resolver: zodResolver(caseRegisterSchema),
    defaultValues: {
      year: new Date().getFullYear(),
      fir_number: '',
      category_node_id: '',
      subject: '',
      file_start_date: '',
      file_end_date: '',
    },
  });

  // Any user change to the case form counts as draft input for autosave.
  // `type === 'change'` filters out programmatic reset() during draft load.
  useEffect(() => {
    const subscription = caseForm.watch((_values, info) => {
      if (info?.type === 'change') dirtySeqRef.current += 1;
    });
    return () => subscription.unsubscribe();
  }, [caseForm]);

  // File Category — an admin-created value master (non-hierarchy template).
  // We pick the lookup template whose name/key mentions "category"; if the
  // admin has exactly one lookup master, that one is used regardless of name.
  const lookupTemplatesQuery = useQuery({
    queryKey: ['lookup-templates'],
    queryFn: () => hierarchyService.getLookupTemplates(),
  });
  const lookupTemplates: { id: number; template_key: string; name: string }[] =
    lookupTemplatesQuery.data?.data?.data ?? [];
  // Only a master that is actually a category counts. Any other lookup master
  // (e.g. KGID) must not become the category field.
  const categoryTemplate = lookupTemplates.find(
    (t) => /categor/i.test(t.name) || /categor/i.test(t.template_key)
  );

  const categoryNodesQuery = useQuery({
    queryKey: ['template-nodes', categoryTemplate?.template_key],
    queryFn: () => hierarchyService.getTemplateNodes(categoryTemplate!.template_key),
    enabled: !!categoryTemplate,
  });
  const categoryOptions: { id: string; name: string }[] = (
    categoryNodesQuery.data?.data?.data ?? []
  ).map((n: any) => ({ id: String(n.id), name: n.label || `#${n.id}` }));
  const categoryLabel = categoryTemplate?.name ?? 'File Category';

  useEffect(() => {
    const draftId = searchParams.get('draft_id');
    if (!draftId) return;
    // Already in sync — e.g. the URL was just updated after (auto)save created
    // the draft. Reloading here would clobber whatever the user typed since.
    if (draftId === currentDraftIdRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await draftService.getDraftById(draftId);
        if (cancelled) return;
        const draft = (res as any)?.data?.data?.draft ?? (res as any)?.data?.draft;
        if (!draft) return;
        setCurrentDraftId(draft.rrms_cases_draft_id);
        currentDraftIdRef.current = draft.rrms_cases_draft_id;
        const draftDocuments = Array.isArray(draft.documents) ? draft.documents : [];
        const fileBackedDraftDocuments = normalizeDraftDocuments(draftDocuments);
        setExistingDraftDocuments(fileBackedDraftDocuments);
        setExistingDraftFileCount(fileBackedDraftDocuments.length);
        const metaDoc =
          draftDocuments.find(
            (d: any) =>
              d.folder_type ||
              d.document_type ||
              d.classification ||
              (Array.isArray(d.hashtags) && d.hashtags.length > 0)
          ) || null;

        caseForm.reset({
          year: draft.year ? Number(draft.year) : new Date().getFullYear(),
          fir_number: draft.fir_number ?? '',
          category_node_id: draft.category_node_id != null ? String(draft.category_node_id) : '',
          subject: draft.subject ?? '',
          file_start_date: draft.file_start_date ?? '',
          file_end_date: draft.file_end_date ?? '',
        });
        setBulkValues({
          classification: metaDoc?.classification === 'Confidential' ? 'Confidential' : 'Public',
          hashtags: Array.isArray(metaDoc?.hashtags) ? metaDoc.hashtags : [],
        });
        // The picker starts blank when editing a draft; the stored placement is
        // kept unless the user picks a different node.
        if (draft.node_id != null) setHierarchyNodeId(Number(draft.node_id));
        // Populating the form from the stored draft is not a user edit.
        savedSeqRef.current = dirtySeqRef.current;
        autosavePausedRef.current = false;
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Failed to load draft.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, caseForm]);

  // CMS-number preview — DISABLED with the upload-form redesign (the number is
  // no longer generated; see the commented buildCmsNumber block in
  // backend/controllers/rrms/caseController.js). Kept for rollback:
  //
  // useEffect(() => {
  //   ...called caseService.previewCmsNumber({ district_id, ps_id, ps_name,
  //   case_type_code, fir_number, year }) and set previewCmsNumber for the
  //   read-only "Case No" field...
  // }, [state, district, ps_name, year, fir_number, case_type, case_status]);

  const handleCaseSearch = async (query: string) => {
    setCaseSearchQuery(query);
    if (!query.trim() || query.length < 3) {
      setCaseSearchResults([]);
      return;
    }
    setIsSearchingCase(true);
    try {
      const results = await caseService.searchCases({ q: query, limit: 10 });
      setCaseSearchResults((results as any).data?.data ?? (results as any).data ?? []);
    } catch {
      setCaseSearchResults([]);
    } finally {
      setIsSearchingCase(false);
    }
  };

  const generateRefMutation = useMutation({
    mutationFn: (data: any) =>
      caseService.generateRefNumber({
        ...data,
        source: 'RRMS',
      }),
  });

  const createCaseMutation = useMutation({
    mutationFn: (payload: any) => caseService.createCase(payload),
  });

  // File management
  const handleFilesAdded = (newFiles: File[]) => {
    const newItems: FileWithMeta[] = newFiles.map((file) => ({
      id: genId(),
      file,
      folder_type: '',
      document_type: '',
      classification: 'Public',
      hashtags: [],
      status: 'idle',
      progress: 0,
    }));
    setFilesWithMeta((prev) => [...prev, ...newItems]);
    markDraftDirty();
  };

  const handleRemoveFile = (id: string) => {
    setFilesWithMeta((prev) => prev.filter((f) => f.id !== id));
    setSelectedIds((prev) => {
      const s = new Set(prev);
      s.delete(id);
      return s;
    });
    markDraftDirty();
  };

  const updateFileRow = (id: string, update: Partial<FileWithMeta>) => {
    setFilesWithMeta((prev) => prev.map((f) => (f.id === id ? { ...f, ...update } : f)));
    markDraftDirty();
  };

  // Multi-select
  const toggleSelectRow = (id: string) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filesWithMeta.length && filesWithMeta.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filesWithMeta.map((f) => f.id)));
    }
  };

  // Bulk apply (classification + hashtags only since the upload-form redesign)
  const applyBulk = () => {
    setFilesWithMeta((prev) =>
      prev.map((f) => {
        if (!selectedIds.has(f.id)) return f;
        return {
          ...f,
          classification: bulkValues.classification,
          hashtags:
            bulkValues.hashtags.length > 0
              ? Array.from(new Set([...f.hashtags, ...bulkValues.hashtags]))
              : f.hashtags,
        };
      })
    );
    setBulkValues({ classification: 'Public', hashtags: [] });
    setSelectedIds(new Set());
    markDraftDirty();
  };

  const toggleSelectExistingRow = (id: string) => {
    setSelectedExistingIds((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const toggleSelectAllExisting = () => {
    if (
      selectedExistingIds.size === existingDraftDocuments.length &&
      existingDraftDocuments.length > 0
    ) {
      setSelectedExistingIds(new Set());
    } else {
      setSelectedExistingIds(
        new Set(existingDraftDocuments.map((d) => d.rrms_document_draft_id))
      );
    }
  };

  const applyExistingBulk = () => {
    setExistingDraftDocuments((prev) =>
      prev.map((d) => {
        if (!selectedExistingIds.has(d.rrms_document_draft_id)) return d;
        return {
          ...d,
          classification: existingBulkValues.classification,
          hashtags:
            existingBulkValues.hashtags.length > 0
              ? Array.from(
                  new Set([
                    ...(Array.isArray(d.hashtags) ? d.hashtags : []),
                    ...existingBulkValues.hashtags,
                  ])
                )
              : d.hashtags,
        };
      })
    );
    setExistingBulkValues({ classification: 'Public', hashtags: [] });
    setSelectedExistingIds(new Set());
    markDraftDirty();
  };

  const getCasePayloadForDraft = () => {
    const caseValues = caseForm.getValues();
    return {
      rrms_ref_number: undefined,
      fir_number: caseValues.fir_number || undefined,
      year: caseValues.year || undefined,
      subject: caseValues.subject || undefined,
      file_start_date: caseValues.file_start_date || undefined,
      file_end_date: caseValues.file_end_date || undefined,
      category_node_id: caseValues.category_node_id
        ? Number(caseValues.category_node_id)
        : undefined,
      node_id: hierarchyNodeIdRef.current ?? undefined,
      division_id: parseInt(localStorage.getItem('rrms_division_id') || '1', 10),
      department_id: parseInt(localStorage.getItem('rrms_department_id') || '1', 10),
    };
  };

  const getDocumentPayloadForDraft = () => {
    return {
      classification: bulkValuesRef.current.classification || undefined,
      hashtags: bulkValuesRef.current.hashtags ?? [],
    };
  };

  const getExistingFileMetadataPayloadForDraft = () =>
    existingDraftDocumentsRef.current.map((d) => ({
      rrms_document_draft_id: d.rrms_document_draft_id,
      classification: d.classification || undefined,
      hashtags: Array.isArray(d.hashtags) ? d.hashtags : [],
    }));

  const updateExistingDraftDocumentRow = (
    id: string,
    update: Partial<ExistingDraftDocument>
  ) => {
    setExistingDraftDocuments((prev) =>
      prev.map((d) =>
        d.rrms_document_draft_id === id ? { ...d, ...update } : d
      )
    );
    markDraftDirty();
  };

  /**
   * Single save routine shared by the manual Save button and the autosave
   * timer. The in-flight guard means two callers can never run concurrently,
   * so a slow createDraft cannot be doubled into two drafts, and files are
   * never appended twice.
   */
  const performDraftSave = (source: 'auto' | 'manual' | 'submit'): Promise<boolean> => {
    if (draftSaveInFlightRef.current) return Promise.resolve(false);
    draftSaveInFlightRef.current = true;
    setIsSavingDraft(true);
    // Captured up front: rows saved by THIS request. Anything the user adds
    // or edits after this point stays pending for the next save.
    const seqAtStart = dirtySeqRef.current;
    const draftIdAtStart = currentDraftIdRef.current;
    const rowsAtStart = filesWithMetaRef.current;
    const promise = (async (): Promise<boolean> => {
      try {
        const filesToSave = rowsAtStart.map((f) => f.file);
        const fileMetadataPayload = rowsAtStart.map((f) => ({
          classification: f.classification || undefined,
          hashtags: f.hashtags ?? [],
        }));
        const casePayload = getCasePayloadForDraft();
        const docPayload = getDocumentPayloadForDraft();
        const existingFileMetadataPayload = getExistingFileMetadataPayloadForDraft();
        const res = draftIdAtStart
          ? await draftService.updateDraft(
              draftIdAtStart,
              filesToSave,
              casePayload,
              docPayload,
              fileMetadataPayload,
              existingFileMetadataPayload
            )
          : await draftService.createDraft(
              filesToSave,
              casePayload,
              docPayload,
              fileMetadataPayload
            );
        const savedDraft = (res as any)?.data?.data?.draft ?? (res as any)?.data?.draft;
        const savedDraftId: string | null =
          savedDraft?.rrms_cases_draft_id ?? draftIdAtStart ?? null;
        if (savedDraftId) {
          currentDraftIdRef.current = savedDraftId;
          setCurrentDraftId(savedDraftId);
        }
        const savedDocuments = Array.isArray(savedDraft?.documents) ? savedDraft.documents : [];
        const savedFileBackedDocs = normalizeDraftDocuments(savedDocuments);
        // Server list is authoritative for WHICH documents exist, but keep
        // classification/hashtags the user edited while this request was in
        // flight — the seq check below queues those edits for the next save.
        setExistingDraftDocuments((prev) => {
          const prevById = new Map(prev.map((d) => [d.rrms_document_draft_id, d]));
          return savedFileBackedDocs.map((d) => {
            const local = prevById.get(d.rrms_document_draft_id);
            return local
              ? { ...d, classification: local.classification, hashtags: local.hashtags }
              : d;
          });
        });
        setExistingDraftFileCount(savedFileBackedDocs.length);
        // Saved files now live in the draft; drop only those rows so files
        // added mid-save are kept (and saved by the next pass).
        const savedRowIds = new Set(rowsAtStart.map((f) => f.id));
        setFilesWithMeta((prev) => prev.filter((f) => !savedRowIds.has(f.id)));
        if (dirtySeqRef.current === seqAtStart) {
          savedSeqRef.current = seqAtStart;
        }
        autosavePausedRef.current = false;
        setLastDraftSavedAt(new Date());
        if (source === 'manual') {
          toast.success(
            draftIdAtStart ? 'Draft updated successfully.' : 'Draft saved successfully.'
          );
        }
        if (!draftIdAtStart && savedDraftId) {
          navigate(`${ROUTES.UPLOAD}?draft_id=${savedDraftId}`, { replace: true });
        }
        return true;
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 404 && draftIdAtStart) {
          // Draft was deleted elsewhere (another tab / drafts page). Stop
          // autosave so we don't resurrect it; a manual Save starts over as
          // a fresh draft.
          autosavePausedRef.current = true;
          if (source === 'manual' || source === 'submit') {
            currentDraftIdRef.current = null;
            setCurrentDraftId(null);
            autosavePausedRef.current = false;
            toast.error(
              'This draft no longer exists. Click Save again to store your work as a new draft.'
            );
          } else {
            toast.error(
              'This draft no longer exists — autosave is paused. Use Save to store your work as a new draft.',
              { id: 'draft-autosave' }
            );
          }
        } else if (source === 'manual') {
          toast.error(err?.response?.data?.message || 'Failed to save draft.');
        } else if (source === 'submit') {
          toast.error(
            err?.response?.data?.message || 'Failed to save draft before submission.'
          );
        } else {
          // Deduped by id so a flaky network doesn't stack a toast per tick.
          toast.error(
            err?.response?.data?.message || 'Draft autosave failed. Will retry.',
            { id: 'draft-autosave' }
          );
        }
        return false;
      } finally {
        draftSaveInFlightRef.current = false;
        draftSavePromiseRef.current = null;
        setIsSavingDraft(false);
      }
    })();
    draftSavePromiseRef.current = promise;
    return promise;
  };

  const handleSaveDraft = async () => {
    await performDraftSave('manual');
  };

  /** At least one substantive input (files, form fields, or placement). The
   * prefilled year alone doesn't count, so a pristine page never creates a
   * draft. */
  const hasMeaningfulDraftInput = () => {
    const v = caseForm.getValues();
    return (
      filesWithMetaRef.current.length > 0 ||
      Boolean(String(v.fir_number || '').trim()) ||
      Boolean(String(v.subject || '').trim()) ||
      Boolean(String(v.category_node_id || '').trim()) ||
      Boolean(String(v.file_start_date || '').trim()) ||
      Boolean(String(v.file_end_date || '').trim()) ||
      hierarchyNodeIdRef.current != null
    );
  };

  // Reassigned every render so the interval callback always sees fresh state.
  autoSaveTickRef.current = () => {
    if (draftSaveInFlightRef.current || isSubmittingRef.current) return;
    if (autosavePausedRef.current) return;
    if (isUploading || uploadComplete) return;
    // Upload-more flow for an existing case: drafts register a NEW case on
    // submit, so autosaving here would end up duplicating the case.
    if (selectedCase) return;
    // Nothing changed since the last successful save.
    if (dirtySeqRef.current === savedSeqRef.current) return;
    if (!currentDraftIdRef.current && !hasMeaningfulDraftInput()) return;
    void performDraftSave('auto');
  };

  useEffect(() => {
    const timer = window.setInterval(
      () => autoSaveTickRef.current(),
      DRAFT_AUTOSAVE_INTERVAL_MS
    );
    return () => window.clearInterval(timer);
  }, []);

  const handleRemoveExistingDraftDocument = async (documentDraftId: string) => {
    if (!currentDraftId) return;
    try {
      setRemovingDraftDocumentId(documentDraftId);
      await draftService.deleteDraftDocument(currentDraftId, documentDraftId);
      setExistingDraftDocuments((prev) =>
        prev.filter((d) => d.rrms_document_draft_id !== documentDraftId)
      );
      setSelectedExistingIds((prev) => {
        const s = new Set(prev);
        s.delete(documentDraftId);
        return s;
      });
      setExistingDraftFileCount((prev) => Math.max(0, prev - 1));
      toast.success('Draft file removed.');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to remove draft file.');
    } finally {
      setRemovingDraftDocumentId(null);
    }
  };
  
  // Validation — per-file metadata is classification + hashtags only now;
  // classification defaults to Public, so files just need to exist.
  const validate = (): boolean => {
    const totalFiles = filesWithMeta.length + existingDraftFileCount;
    if (totalFiles === 0) {
      toast.error('Add at least one file');
      return false;
    }
    return true;
  };

  const performUpload = async (caseId: string) => {
    if (!caseId) {
      toast.error('No case selected');
      return;
    }
    setIsUploading(true);
    setFilesWithMeta((prev) =>
      prev.map((f) => ({ ...f, status: 'uploading' as const, progress: 0 }))
    );
    try {
      const uploadRes = await documentService.uploadDocuments(
        caseId,
        filesWithMeta,
        (progress) => {
          setFilesWithMeta((prev) =>
            prev.map((f) => (f.status === 'uploading' ? { ...f, progress } : f))
          );
        }
      );
      setFilesWithMeta((prev) =>
        prev.map((f) => ({ ...f, status: 'success' as const, progress: 100 }))
      );
      setUploadedCount(filesWithMeta.length);
      toast.success(
        `${filesWithMeta.length} file${filesWithMeta.length !== 1 ? 's' : ''} uploaded successfully`
      );

      // Auto-approve documents uploaded by a reviewer (records.approve) —
      // their uploads bypass the pending-review step. The backend re-checks
      // the permission per record node, so this is a UX shortcut only.
      if (can('records.approve', ['RRMS_CONTENT_MANAGER'])) {
        const uploadedDocs: any[] =
          (uploadRes as any)?.data?.data?.documents ??
          (uploadRes as any)?.data?.documents ??
          [];
        if (uploadedDocs.length > 0) {
          await Promise.allSettled(
            uploadedDocs.map((doc: any) =>
              approvalService.decideDocument(doc.document_id, 'Approved', '')
            )
          );
        }
      }

      // Refresh existing documents list so that "Upload More"
      // immediately shows the newly uploaded files.
      await reloadExistingCaseDocs();

      setUploadComplete(true);
    } catch (err: any) {
      const msg = err.response?.data?.message ?? 'Upload failed. Please try again.';
      toast.error(msg);
      setFilesWithMeta((prev) =>
        prev.map((f) =>
          f.status === 'uploading' ? { ...f, status: 'error' as const, errorMessage: msg } : f
        )
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleRegisterAndUpload = async () => {
    if (!validate()) return;
    // Block autosave ticks for the entire submit flow — a save landing
    // between updateDraft and submitDraft (or while a new case is being
    // registered) could re-append files or leave an orphan draft behind.
    isSubmittingRef.current = true;
    try {
      await runRegisterAndUpload();
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const runRegisterAndUpload = async () => {
    // Let an in-flight (auto)save settle first: it may still be creating the
    // draft or attaching files that must not be sent a second time.
    if (draftSavePromiseRef.current) {
      await draftSavePromiseRef.current;
    }
    const draftId = currentDraftIdRef.current;
    if (draftId) {
      // Submission promotes the draft to a case, so the case form must be
      // complete (drafts themselves may stay partial — only submit is gated).
      const caseValid = await caseForm.trigger();
      if (!caseValid) {
        const caseMsg = getFirstCaseFormErrorMessage(
          caseForm.formState.errors as Record<string, any>
        );
        toast.error(caseMsg || 'Please fill all required case details.');
        return;
      }
      // Save through the shared routine so its bookkeeping applies: saved
      // file rows are cleared immediately, so a failed submitDraft below
      // can be retried without re-appending the same files to the draft.
      const saved = await performDraftSave('submit');
      if (!saved) return;
      try {
        const submitRes = await draftService.submitDraft(draftId);
        const submittedCase = (submitRes as any)?.data?.data?.case ?? (submitRes as any)?.data?.case;
        const submittedDocs = (submitRes as any)?.data?.data?.documents ?? (submitRes as any)?.data?.documents ?? [];
        setSelectedCase(submittedCase ?? null);
        setUploadedCount(Array.isArray(submittedDocs) ? submittedDocs.length : 0);
        setUploadComplete(true);
        setFilesWithMeta([]);
        setCurrentDraftId(null);
        currentDraftIdRef.current = null;
        // Submitted work is no longer draft-pending; autosave must not
        // recreate a draft from the leftover form values.
        savedSeqRef.current = dirtySeqRef.current;
        return;
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Failed to submit draft.');
        return;
      }
    }
    
    // Branch A: case already selected (prefilled from navigation or search)
    if (selectedCase) {
      await performUpload(String(selectedCase.case_id));
      return;
    }

    // Branch B: register new case first, then upload
    if (hierarchyAvailable && hierarchyNodeId == null) {
      toast.error('Please select where this case belongs in the hierarchy.');
      return;
    }
    const caseValid = await caseForm.trigger();
    if (!caseValid) {
      const caseMsg = getFirstCaseFormErrorMessage(
        caseForm.formState.errors as Record<string, any>
      );
      toast.error(caseMsg || 'Please fill all required case details.');
      return;
    }
    // The category is only mandatory where the admin defined a category master.
    if (categoryTemplate && !String(caseForm.getValues().category_node_id || '').trim()) {
      toast.error(`${categoryLabel} is required.`);
      return;
    }

    try {
      const caseValues = caseForm.getValues();

      const firDigits = String(caseValues.fir_number || '')
        .replace(/\D/g, '')
        .slice(-4);

      // The 17-digit ref number is now derived from the dynamic hierarchy:
      // top-level node → district slot, deepest node → PS slot, category →
      // 2-digit prefix, file number digits → FIR slot. Same format/uniqueness
      // registry as before, no CID masters involved.
      const categoryId = Number(caseValues.category_node_id) || 0;
      const refRes = await generateRefMutation.mutateAsync({
        district_id: String(hierarchyPath[0] ?? hierarchyNodeId ?? 0),
        ps_id: String(hierarchyNodeId ?? 0),
        year: caseValues.year,
        case_type_code: String(categoryId % 100).padStart(2, '0'),
        fir_number: firDigits,
      });

      const rrmsRef =
        (refRes as any)?.data?.data?.ref_number ??
        (refRes as any)?.data?.ref_number ??
        '';

      if (!rrmsRef) {
        toast.error('Failed to generate reference number.');
        return;
      }

      const createRes = await createCaseMutation.mutateAsync({
        rrms_ref_number: rrmsRef,
        fir_number: caseValues.fir_number,
        year: caseValues.year,
        subject: caseValues.subject,
        file_start_date: caseValues.file_start_date || undefined,
        file_end_date: caseValues.file_end_date || undefined,
        category_node_id: categoryId || undefined,
        // Dynamic hierarchy placement (admin master); governs record visibility
        // and the storage location (Section/Sub-Section/Category/Year/FileNo).
        ...(hierarchyNodeId != null ? { node_id: hierarchyNodeId } : {}),
        division_id: parseInt(localStorage.getItem('rrms_division_id') || '1', 10),
        department_id: parseInt(localStorage.getItem('rrms_department_id') || '1', 10),
      });

      const newCase =
        (createRes as any)?.data?.data?.case ?? (createRes as any)?.data?.case ?? null;

      if (!newCase) {
        toast.error('Failed to register case.');
        return;
      }

      setSelectedCase(newCase);
      await performUpload(String(newCase.case_id));
    } catch (err: any) {
      toast.error(
        getApiValidationMessage(err, 'Failed to register case and upload documents.', {
          rawFirInput: caseForm.getValues('fir_number'),
        })
      );
    }
  };

  if (uploadComplete) {
    return (
      <div className="">
        <div
          className="card mx-auto text-center"
          style={{
            maxWidth: 480,
            marginTop: 80,
            marginBottom: 80,
            padding: 'var(--spacing-8)',
          }}
        >
          <CheckCircle2
            size={56}
            style={{
              color: 'var(--color-success)',
              margin: '0 auto var(--spacing-4)',
              display: 'block',
            }}
          />
          <h2
            className="text-2xl font-bold mb-2"
            style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: 700,
              marginBottom: 'var(--spacing-2)',
            }}
          >
            Upload Successful
          </h2>
          <p
            className="text-muted mb-6"
            style={{
              color: 'var(--color-gray-500)',
              marginBottom: 'var(--spacing-6)',
            }}
          >
            {uploadedCount} file{uploadedCount !== 1 ? 's' : ''} uploaded to{' '}
            <strong>
              {/* CMS number hidden with the upload-form redesign — identify by File No / ref number */}
              {selectedCase?.fir_number
                ? `File No. ${selectedCase.fir_number}`
                : selectedCase?.rrms_ref_number || 'the case'}
            </strong>
          </p>
          <div
            className="flex gap-3 justify-center flex-wrap"
            style={{
              display: 'flex',
              gap: 'var(--spacing-3)',
              justifyContent: 'center',
            }}
          >
            <button
              type="button"
              onClick={() => {
                setFilesWithMeta([]);
                setSelectedIds(new Set());
                setUploadComplete(false);
                setUploadedCount(0);
                setBulkValues({ classification: 'Public', hashtags: [] });
                // When user chooses "Upload More" after a successful upload,
                // make sure we have the latest documents for this case.
                reloadExistingCaseDocs();
              }}
              className="rounded-md border border-primary px-5 py-3 text-md font-medium text-primary bg-transparent hover:bg-primary-50"
            >
              Upload More
            </button>
            <button
              type="button"
              onClick={() => navigate(ROUTES.UPLOAD_STATUS)}
              className="rounded-md bg-primary px-5 py-3 text-md font-medium text-white hover:bg-primary-light"
            >
              View Upload Status
            </button>
            {selectedCase && (
              <button
                type="button"
                onClick={() =>
                  navigate(
                    ROUTES.CASE_DETAIL.replace(
                      ':caseId',
                      String(selectedCase.case_id)
                    )
                  )
                }
                className="rounded-md border border-[var(--color-gray-300)] px-5 py-3 text-md font-medium text-[var(--color-gray-800)] bg-white hover:bg-[var(--color-gray-50)]"
              >
                View Case Documents
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <PageHeader
        title={currentDraftId ? 'Edit Draft' : 'Upload New Case Document'}
        subtitle={
          currentDraftId
            ? 'Update your draft details and submit when ready'
            : 'Capture case details and upload case documents to RRMS'
        }
      />
      {/* SECTION 1: CASE DETAILS */}
      <div style={{ width: '100%', marginBottom: 'var(--spacing-6)', }}>
        <div className="card p-6" style={{ width: '100%' }}>

          {/* Search Existing Case */}
          <div style={{ marginBottom: 'var(--spacing-6)',  display: 'none' }}>
            <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--spacing-3)' }}>
              Search Existing Case
            </h3>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={caseSearchQuery}
                onChange={(e) => handleCaseSearch(e.target.value)}
                placeholder="Search by CMS number or FIR number..."
                style={{
                  width: '100%', padding: 'var(--spacing-3) var(--spacing-4)',
                  border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)', boxSizing: 'border-box'
                }}
              />
              {isSearchingCase && (
                <span style={{ position: 'absolute', right: 'var(--spacing-3)', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-gray-400)', fontSize: 'var(--font-size-xs)' }}>
                  Searching...
                </span>
              )}
            </div>
            {caseSearchResults.length > 0 && (
              <div style={{
                marginTop: 'var(--spacing-2)', border: '1px solid var(--color-gray-200)',
                borderRadius: 'var(--radius-md)', overflow: 'hidden', maxHeight: '280px', overflowY: 'auto'
              }}>
                {caseSearchResults.map((c) => (
                  <button
                    key={c.case_id}
                    type="button"
                    onClick={() => {
                      setSelectedCase(c);
                      setCaseSearchQuery('');
                      setCaseSearchResults([]);
                      toast.success(`Case ${c.cms_number} selected`);
                    }}
                    style={{
                      width: '100%', textAlign: 'left', padding: 'var(--spacing-3) var(--spacing-4)',
                      background: 'white', border: 'none', borderBottom: '1px solid var(--color-gray-100)',
                      cursor: 'pointer', fontSize: 'var(--font-size-sm)'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-primary-50)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                  >
                    <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)' }}>
                      {c.cms_number}
                    </span>
                    <span style={{ color: 'var(--color-gray-500)', marginLeft: 'var(--spacing-2)' }}>
                      FIR: {c.fir_number} &nbsp;·&nbsp; {c.ps_name} &nbsp;·&nbsp; {c.case_type}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {selectedCase && (
              <div style={{
                marginTop: 'var(--spacing-2)', padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'var(--color-success-light)', border: '1px solid var(--color-success)',
                borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-success)', fontWeight: 'var(--font-weight-semibold)' }}>
                  ✓ Selected: {selectedCase.cms_number}
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedCase(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-500)', fontSize: 'var(--font-size-xs)' }}
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* OR divider */}
          {!selectedCase && (
            <div style={{ display: 'none', alignItems: 'center', gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-6)' }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--color-gray-200)' }} />
              <span style={{ color: 'var(--color-gray-400)', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)' }}>OR</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--color-gray-200)' }} />
            </div>
          )}

          {selectedCase && (
          <>
          <h3
            className="font-semibold mb-5"
            style={{
              marginBottom: 'var(--spacing-5)',
              fontWeight: 600,
            }}
          >
            Case Details (Read Only)
          </h3>

          <form
            className="flex flex-col gap-4"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-4)',
            }}
          >
            {/* Upload-form redesign: the record is identified by File No / Year /
                Subject / dates. Legacy CID fields and the CMS number are no
                longer shown (CMS generation is commented out server-side). */}
            <div
              className="grid gap-3"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 'var(--spacing-3)',
              }}
            >
              <div>
                <label className="block text-base font-semibold mb-1">File Number</label>
                <input
                  readOnly
                  value={selectedCase.fir_number || ''}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-md bg-gray-50 text-gray-500"
                />
              </div>
              <div>
                <label className="block text-base font-semibold mb-1">Year</label>
                <input
                  readOnly
                  value={selectedCase.year || ''}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-md bg-gray-50 text-gray-500"
                />
              </div>
              <div>
                <label className="block text-base font-semibold mb-1">RRMS Ref Number</label>
                <input
                  readOnly
                  value={selectedCase.rrms_ref_number || ''}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-md bg-gray-50 text-gray-500"
                />
              </div>
            </div>

            <div
              className="grid gap-3"
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr',
                gap: 'var(--spacing-3)',
              }}
            >
              <div>
                <label className="block text-base font-semibold mb-1">Subject</label>
                <input
                  readOnly
                  value={(selectedCase as any).subject || ''}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-md bg-gray-50 text-gray-500"
                />
              </div>
              <div>
                <label className="block text-base font-semibold mb-1">File Start Date</label>
                <input
                  readOnly
                  value={
                    (selectedCase as any).file_start_date
                      ? String((selectedCase as any).file_start_date).slice(0, 10)
                      : ''
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-md bg-gray-50 text-gray-500"
                />
              </div>
              <div>
                <label className="block text-base font-semibold mb-1">File End Date</label>
                <input
                  readOnly
                  value={
                    (selectedCase as any).file_end_date
                      ? String((selectedCase as any).file_end_date).slice(0, 10)
                      : ''
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-md bg-gray-50 text-gray-500"
                />
              </div>
            </div>
          </form>
          </>
          )}

          {!selectedCase && (
          <>
          <h3
            className="font-semibold mb-5"
            style={{
              marginBottom: 'var(--spacing-5)',
              fontWeight: 600,
            }}
          >
            Register New Case
          </h3>

          <form
            className="flex flex-col gap-4"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-4)',
            }}
          >
            {/* Hierarchy placement — levels & options come from the admin master
                (RBAC-scoped); renders nothing when no hierarchy is defined. */}
            {/* Hierarchy placement (e.g. Section → Sub-Section): levels & options
                come from the admin master, RBAC-scoped per user. */}
            <HierarchyNodePicker
              permission="upload"
              required
              onChange={(nodeId) => {
                setHierarchyNodeId(nodeId);
                markDraftDirty();
              }}
              onPathChange={setHierarchyPath}
              onAvailability={setHierarchyAvailable}
            />
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              <div>
                <label className="block text-base font-semibold mb-1">
                  Year <span className="text-error">*</span>
                </label>
                <Controller
                  name="year"
                  control={caseForm.control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="number"
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value, 10) || 0)
                      }
                      className="w-full px-3 py-2 border border-gray-200 rounded-md text-md"
                    />
                  )}
                />
                {caseForm.formState.errors.year && (
                  <p className="text-sm text-error mt-1">
                    {caseForm.formState.errors.year.message}
                  </p>
                )}
              </div>
            {/* </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"> */}
              <div>
                <label className="block text-base font-semibold mb-1">
                  File Number <span className="text-error">*</span>
                </label>
                {/* Stored in the fir_number column (reused); free text now. */}
                <input
                  {...caseForm.register('fir_number')}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-md"
                  placeholder="e.g. 42/2026 or FN-0042"
                />
                {caseForm.formState.errors.fir_number && (
                  <p className="text-sm text-error mt-1">
                    {caseForm.formState.errors.fir_number.message}
                  </p>
                )}
              </div>
              {/* Only rendered when the admin has created a category master;
                  deployments without categories don't get an unusable field. */}
              {categoryTemplate && (
                <div>
                  <label className="block text-base font-semibold mb-1">
                    {categoryLabel} <span className="text-error">*</span>
                  </label>
                  {/* Values come from the admin-created lookup master (template
                      nodes) — add/rename categories in the Admin module. */}
                  <Controller
                    name="category_node_id"
                    control={caseForm.control}
                    render={({ field }) => (
                      <SearchableSelect
                        valueKey="id"
                        options={categoryOptions}
                        value={field.value ? String(field.value) : ''}
                        onChange={field.onChange}
                        placeholder={`Select ${categoryLabel.toLowerCase()}`}
                        loading={lookupTemplatesQuery.isLoading || categoryNodesQuery.isLoading}
                      />
                    )}
                  />
                  {caseForm.formState.errors.category_node_id && (
                    <p className="text-sm text-error mt-1">
                      {caseForm.formState.errors.category_node_id.message}
                    </p>
                  )}
                </div>
              )}
            {/* </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"> */}
              <div>
                <label className="block text-base font-semibold mb-1">
                  File Start Date <span className="text-error">*</span>
                </label>
                <Controller
                  name="file_start_date"
                  control={caseForm.control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="date"
                      className="w-full px-3 py-2 border border-gray-200 rounded-md text-md"
                    />
                  )}
                />
                {caseForm.formState.errors.file_start_date && (
                  <p className="text-sm text-error mt-1">
                    {caseForm.formState.errors.file_start_date.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-base font-semibold mb-1">
                  File End Date
                </label>
                <Controller
                  name="file_end_date"
                  control={caseForm.control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="date"
                      className="w-full px-3 py-2 border border-gray-200 rounded-md text-md"
                    />
                  )}
                />
                {caseForm.formState.errors.file_end_date && (
                  <p className="text-sm text-error mt-1">
                    {caseForm.formState.errors.file_end_date.message}
                  </p>
                )}
              </div>
            </div>

            <div
              className="grid gap-3"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 'var(--spacing-3)',
              }}
            >
              <div>
                <label className="block text-base font-semibold mb-1">
                  Subject <span className="text-error">*</span>
                </label>
                <input
                  {...caseForm.register('subject')}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-md"
                  placeholder="Brief subject / description of the file"
                />
                {caseForm.formState.errors.subject && (
                  <p className="text-sm text-error mt-1">
                    {caseForm.formState.errors.subject.message}
                  </p>
                )}
              </div>
            </div>

            {/* CMS "Case No" preview — HIDDEN with the upload-form redesign.
                Generation is commented out in caseController.createCase; the
                preview effect above is likewise commented for rollback. */}
          </form>
          </>
          )}
        </div>
      </div>

      {/* SECTION 2: DOCUMENT UPLOAD — per-file metadata */}
      <div style={{ width: '100%' }}>
        <div className="card p-6" style={{ width: '100%' }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleRegisterAndUpload();
            }}
          >
            <div
              className="card p-6 flex flex-col gap-5"
              style={{
                padding: 'var(--spacing-6)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-5)',
              }}
            >
              <FileUploadZone
                onFilesAdded={handleFilesAdded}
                currentCount={filesWithMeta.length + existingDraftFileCount}
                disabled={isUploading}
              />
              {currentDraftId && (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                  Existing draft files: {existingDraftFileCount}. New files added here will be appended to the draft.
                </p>
              )}
              {currentDraftId && existingDraftDocuments.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-3)',
                  }}
                >
                  {(() => {
                    const allExistingSelected =
                      existingDraftDocuments.length > 0 &&
                      selectedExistingIds.size === existingDraftDocuments.length;
                    const someExistingSelected =
                      selectedExistingIds.size > 0 &&
                      selectedExistingIds.size < existingDraftDocuments.length;
                    return (
                      <>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 'var(--spacing-4)',
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            background: 'var(--color-gray-50)',
                            border: '1px solid var(--color-gray-200)',
                            borderRadius: 'var(--radius-md)',
                            flexWrap: 'wrap',
                          }}
                        >
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              cursor: 'pointer',
                              fontSize: 'var(--font-size-sm)',
                              fontWeight: 500,
                              color: 'var(--color-gray-700)',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={allExistingSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = someExistingSelected;
                              }}
                              onChange={toggleSelectAllExisting}
                              disabled={isUploading}
                              style={{ width: 16, height: 16, cursor: 'pointer' }}
                            />
                            <span>
                              {selectedExistingIds.size > 0
                                ? `${selectedExistingIds.size} selected · Apply bulk values`
                                : 'Select / deselect all'}
                            </span>
                          </label>

                          {selectedExistingIds.size > 0 && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-3)',
                                flexWrap: 'wrap',
                              }}
                            >
                              {/* Folder/Document Type dropped — classification + hashtags only */}
                              <div style={{ display: 'flex', gap: 4 }}>
                                {(['Public', 'Confidential'] as const).map((opt) => (
                                  <button
                                    key={opt}
                                    type="button"
                                    onClick={() =>
                                      setExistingBulkValues((prev) => ({
                                        ...prev,
                                        classification: opt,
                                      }))
                                    }
                                    style={{
                                      padding: '6px 10px',
                                      fontSize: 'var(--font-size-xs)',
                                      fontWeight: 500,
                                      borderRadius: 'var(--radius-md)',
                                      border: `1px solid ${
                                        existingBulkValues.classification === opt
                                          ? opt === 'Confidential'
                                            ? 'var(--color-error)'
                                            : 'var(--color-primary)'
                                          : 'var(--color-gray-200)'
                                      }`,
                                      background:
                                        existingBulkValues.classification === opt
                                          ? opt === 'Confidential'
                                            ? '#fef2f2'
                                            : 'var(--color-primary-50)'
                                          : 'white',
                                      color:
                                        existingBulkValues.classification === opt
                                          ? opt === 'Confidential'
                                            ? 'var(--color-error)'
                                            : 'var(--color-primary)'
                                          : 'var(--color-gray-600)',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 3,
                                    }}
                                  >
                                    {opt === 'Public' ? <Globe size={11} /> : <Lock size={11} />}
                                    {opt}
                                  </button>
                                ))}
                              </div>

                              <button
                                type="button"
                                onClick={applyExistingBulk}
                                style={{
                                  padding: '6px 16px',
                                  fontSize: 'var(--font-size-sm)',
                                  fontWeight: 600,
                                  borderRadius: 'var(--radius-md)',
                                  background: 'var(--color-primary)',
                                  color: 'white',
                                  border: 'none',
                                  cursor: 'pointer',
                                }}
                              >
                                Apply
                              </button>
                              <button
                                type="button"
                                onClick={() => setSelectedExistingIds(new Set())}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: 'var(--font-size-sm)',
                                  borderRadius: 'var(--radius-md)',
                                  background: 'transparent',
                                  color: 'var(--color-gray-600)',
                                  border: '1px solid var(--color-gray-200)',
                                  cursor: 'pointer',
                                }}
                              >
                                Clear
                              </button>
                            </div>
                          )}
                        </div>
                        {existingDraftDocuments.map((doc) => (
                    (() => {
                      const isSelected = selectedExistingIds.has(
                        doc.rrms_document_draft_id
                      );
                      return (
                        <div
                          key={doc.rrms_document_draft_id}
                          style={{
                            display: 'grid',
                            gridTemplateColumns:
                              '32px minmax(0,1.9fr) minmax(0,1fr) minmax(0,1.4fr) 96px',
                            alignItems: 'start',
                            gap: 'var(--spacing-2)',
                            padding: 'var(--spacing-3)',
                            border: `1px solid ${
                              isSelected
                                ? 'var(--color-primary)'
                                : 'var(--color-gray-200)'
                            }`,
                            borderRadius: 'var(--radius-md)',
                            background: isSelected
                              ? 'var(--color-primary-50)'
                              : 'var(--color-gray-50)',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', paddingTop: 8 }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() =>
                                toggleSelectExistingRow(doc.rrms_document_draft_id)
                              }
                              disabled={isUploading}
                              style={{ width: 15, height: 15, cursor: 'pointer' }}
                            />
                          </div>
                          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <p
                              style={{
                                margin: 0,
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                color: 'var(--color-gray-800)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {doc.original_file_name || 'Draft file'}
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-gray-500)',
                              }}
                            >
                              {formatFileSize(doc.file_size_bytes)}
                            </p>
                          </div>
                          {/* Folder/Document Type dropped — classification + hashtags only */}
                          <div>
                            <label
                              style={{
                                display: 'block',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 600,
                                color: 'var(--color-gray-600)',
                                marginBottom: 6,
                              }}
                            >
                              Classification
                            </label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {(['Public', 'Confidential'] as const).map((opt) => (
                                <button
                                  key={opt}
                                  type="button"
                                  disabled={isUploading}
                                  onClick={() =>
                                    updateExistingDraftDocumentRow(
                                      doc.rrms_document_draft_id,
                                      { classification: opt }
                                    )
                                  }
                                  style={{
                                    flex: 1,
                                    padding: '6px 6px',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 500,
                                    borderRadius: 'var(--radius-md)',
                                    border: `1px solid ${
                                      (doc.classification || 'Public') === opt
                                        ? opt === 'Confidential'
                                          ? 'var(--color-error)'
                                          : 'var(--color-primary)'
                                        : 'var(--color-gray-200)'
                                    }`,
                                    background:
                                      (doc.classification || 'Public') === opt
                                        ? opt === 'Confidential'
                                          ? '#fef2f2'
                                          : 'var(--color-primary-50)'
                                        : 'white',
                                    color:
                                      (doc.classification || 'Public') === opt
                                        ? opt === 'Confidential'
                                          ? 'var(--color-error)'
                                          : 'var(--color-primary)'
                                        : 'var(--color-gray-600)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 3,
                                  }}
                                >
                                  {opt === 'Public' ? <Globe size={10} /> : <Lock size={10} />}
                                  {opt}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label
                              style={{
                                display: 'block',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 600,
                                color: 'var(--color-gray-600)',
                                marginBottom: 6,
                              }}
                            >
                              Hashtags (optional)
                            </label>
                            <HashtagInput
                              value={Array.isArray(doc.hashtags) ? doc.hashtags : []}
                              onChange={(tags) =>
                                updateExistingDraftDocumentRow(
                                  doc.rrms_document_draft_id,
                                  { hashtags: tags }
                                )
                              }
                              maxTags={5}
                              placeholder="Add tag..."
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveExistingDraftDocument(
                                doc.rrms_document_draft_id
                              )
                            }
                            disabled={
                              isUploading ||
                              removingDraftDocumentId === doc.rrms_document_draft_id
                            }
                            className="rounded-md border border-[var(--color-error)] px-3 py-1 text-xs font-medium text-[var(--color-error)] bg-white disabled:opacity-60 self-center"
                          >
                            {removingDraftDocumentId === doc.rrms_document_draft_id
                              ? 'Removing...'
                              : 'Remove'}
                          </button>
                        </div>
                      );
                    })()
                  ))}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Existing case documents when Upload More is used */}
              {selectedCase && existingCaseDocuments.length > 0 && (
                <div
                  style={{
                    marginTop: 'var(--spacing-4)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-3)',
                  }}
                >
                  <h3
                    style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 600,
                      marginBottom: 'var(--spacing-1)',
                    }}
                  >
                    Existing Documents for this Case
                  </h3>
                  <p
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-gray-500)',
                      marginBottom: 'var(--spacing-2)',
                    }}
                  >
                    You can add more hashtags and change classification from Public to Confidential
                    for documents you uploaded. Confidential documents cannot be reverted to Public.
                  </p>
                  {isLoadingExistingCaseDocs ? (
                    <p
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-gray-500)',
                      }}
                    >
                      Loading existing documents...
                    </p>
                  ) : (
                    existingCaseDocuments.map((doc) => {
                      const isOwner =
                        authUserId != null &&
                        Number(doc.uploaded_by_user_id) === Number(authUserId);
                      const isConfidential = doc.classification === 'Confidential';
                      const isEditing = editingCaseDocumentId === doc.document_id;
                      const effectiveClassification = isEditing
                        ? editingCaseDocClassification
                        : (doc.classification as 'Public' | 'Confidential');
                      const effectiveHashtags = isEditing
                        ? editingCaseDocHashtags
                        : Array.isArray(doc.hashtags)
                          ? doc.hashtags
                          : [];
                      const displayFileName = formatDocumentDisplayName({
                        cmsNumber: selectedCase?.cms_number,
                        originalFileName: doc.original_file_name,
                        documentType: doc.document_type,
                      });

                      return (
                        <div
                          key={doc.document_id}
                          style={{
                            padding: '12px 16px',
                            marginBottom: 8,
                            borderRadius: 'var(--radius-md)',
                            border: isEditing
                              ? '1px solid var(--color-primary-200)'
                              : '1px solid var(--color-gray-200)',
                            background: 'var(--color-surface, #fff)',
                            boxShadow: isEditing
                              ? '0 2px 8px rgba(0,0,0,0.08)'
                              : 'none',
                            transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                          }}
                        >
                          {/* View mode header row */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 12,
                            }}
                          >
                            {/* Icon */}
                            <div
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                background: 'var(--color-gray-100)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              {getFileIconForExtension(doc.file_extension, 20)}
                            </div>

                            {/* Info + hashtags */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: 'var(--font-size-sm)',
                                  fontWeight: 600,
                                  color: 'var(--color-gray-800)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                                title={displayFileName}
                              >
                                {displayFileName}
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: 'var(--font-size-xs)',
                                  color: 'var(--color-gray-500)',
                                  marginBottom:
                                    effectiveHashtags && effectiveHashtags.length
                                      ? 4
                                      : 0,
                                }}
                              >
                                {doc.folder_type} · {doc.document_type}
                              </p>
                              {effectiveHashtags && effectiveHashtags.length > 0 && (
                                <div
                                  style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 4,
                                  }}
                                >
                                  {effectiveHashtags.map((tag) => (
                                    <span
                                      key={tag}
                                      style={{
                                        fontSize: 'var(--font-size-xs)',
                                        background: 'var(--color-gray-100)',
                                        color: 'var(--color-gray-600)',
                                        borderRadius: 4,
                                        padding: '1px 6px',
                                      }}
                                    >
                                      #{tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Badges + Edit button */}
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flexShrink: 0,
                              }}
                            >
                              {/* Classification badge */}
                              <span
                                style={{
                                  fontSize: 'var(--font-size-2xs)',
                                  padding: '2px 8px',
                                  borderRadius: 999,
                                  background:
                                    doc.classification === 'Confidential'
                                      ? '#fef2f2'
                                      : 'var(--color-primary-50)',
                                  color:
                                    doc.classification === 'Confidential'
                                      ? 'var(--color-error)'
                                      : 'var(--color-primary)',
                                  border:
                                    doc.classification === 'Confidential'
                                      ? '1px solid var(--color-error-200)'
                                      : '1px solid var(--color-primary-200)',
                                }}
                              >
                                {doc.classification || 'Public'}
                              </span>

                              {/* Approval status badge */}
                              <span
                                style={{
                                  fontSize: 'var(--font-size-2xs)',
                                  padding: '2px 8px',
                                  borderRadius: 999,
                                  background: 'var(--color-gray-100)',
                                  color: 'var(--color-gray-700)',
                                  border: '1px solid var(--color-gray-200)',
                                  textTransform: 'capitalize',
                                }}
                              >
                                {doc.approval_status || 'Pending'}
                              </span>

                              {isOwner && !isEditing && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingCaseDocumentId(doc.document_id);
                                    setEditingCaseDocClassification(
                                      (doc.classification as
                                        | 'Public'
                                        | 'Confidential') || 'Public'
                                    );
                                    setEditingCaseDocHashtags(
                                      Array.isArray(doc.hashtags)
                                        ? doc.hashtags
                                        : []
                                    );
                                  }}
                                  disabled={isUploading}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    padding: '4px 10px',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 500,
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--color-gray-300)',
                                    background: 'white',
                                    color: 'var(--color-gray-700)',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <Pencil size={12} />
                                  Edit
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Edit mode section */}
                          {isOwner && isEditing && (
                            <>
                              <div
                                style={{
                                  borderTop: '1px dashed var(--color-gray-200)',
                                  marginTop: 10,
                                  marginBottom: 10,
                                }}
                              />
                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
                                  gap: 16,
                                }}
                              >
                                {/* Classification editor */}
                                <div>
                                  <label
                                    style={{
                                      display: 'block',
                                      fontSize: 'var(--font-size-xs)',
                                      fontWeight: 600,
                                      color: 'var(--color-gray-600)',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.05em',
                                      marginBottom: 8,
                                    }}
                                  >
                                    Classification
                                  </label>

                                  {isConfidential ? (
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '8px 12px',
                                        background: '#fef2f2',
                                        border:
                                          '1px solid var(--color-error-200)',
                                        borderRadius: 'var(--radius-sm)',
                                        fontSize: 'var(--font-size-sm)',
                                        color: 'var(--color-error)',
                                      }}
                                    >
                                      <Lock size={13} />
                                      Confidential
                                      <span
                                        style={{
                                          marginLeft: 'auto',
                                          fontSize: 'var(--font-size-xs)',
                                          color: 'var(--color-gray-500)',
                                        }}
                                      >
                                        Cannot be downgraded
                                      </span>
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 6,
                                      }}
                                    >
                                      {/* Public (informational) */}
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 8,
                                          padding: '8px 12px',
                                          border:
                                            effectiveClassification === 'Public'
                                              ? '1px solid var(--color-primary)'
                                              : '1px solid var(--color-gray-200)',
                                          borderRadius: 'var(--radius-sm)',
                                          background:
                                            effectiveClassification === 'Public'
                                              ? 'var(--color-primary-50)'
                                              : 'white',
                                          fontSize: 'var(--font-size-sm)',
                                        }}
                                      >
                                        <Globe
                                          size={13}
                                          style={{ color: 'var(--color-primary)' }}
                                        />
                                        Public
                                      </div>

                                      {/* Confidential selectable */}
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setEditingCaseDocClassification(
                                            'Confidential'
                                          )
                                        }
                                        disabled={isUploading}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 8,
                                          padding: '8px 12px',
                                          border:
                                            effectiveClassification ===
                                            'Confidential'
                                              ? '1px solid var(--color-error-400)'
                                              : '1px solid var(--color-gray-200)',
                                          borderRadius: 'var(--radius-sm)',
                                          background:
                                            effectiveClassification ===
                                            'Confidential'
                                              ? '#fef2f2'
                                              : 'white',
                                          fontSize: 'var(--font-size-sm)',
                                          color:
                                            effectiveClassification ===
                                            'Confidential'
                                              ? 'var(--color-error)'
                                              : 'var(--color-gray-700)',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        <Lock
                                          size={13}
                                          style={{ color: 'var(--color-error)' }}
                                        />
                                        Confidential
                                      </button>

                                      {effectiveClassification ===
                                        'Confidential' && (
                                        <div
                                          style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 6,
                                            padding: '6px 10px',
                                            background: 'var(--color-warning-50)',
                                            border:
                                              '1px solid var(--color-warning-200)',
                                            borderRadius: 'var(--radius-sm)',
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-warning-700)',
                                          }}
                                        >
                                          <AlertTriangle
                                            size={12}
                                            style={{ marginTop: 1 }}
                                          />
                                          <span>
                                            Approval status will be reset to
                                            Pending.
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Hashtags editor */}
                                <div>
                                  <label
                                    style={{
                                      display: 'block',
                                      fontSize: 'var(--font-size-xs)',
                                      fontWeight: 600,
                                      color: 'var(--color-gray-600)',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.05em',
                                      marginBottom: 8,
                                    }}
                                  >
                                    Hashtags{' '}
                                    <span
                                      style={{
                                        fontWeight: 400,
                                        color: 'var(--color-gray-400)',
                                      }}
                                    >
                                      (optional)
                                    </span>
                                  </label>
                                  <HashtagInput
                                    value={effectiveHashtags}
                                    onChange={(tags) => {
                                      if (!isOwner) return;
                                      setEditingCaseDocHashtags(tags);
                                    }}
                                    maxTags={5}
                                    placeholder="Add tag..."
                                  />
                                </div>
                              </div>

                              {/* Actions */}
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  gap: 8,
                                  marginTop: 12,
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => setEditingCaseDocumentId(null)}
                                  disabled={isUploading}
                                  className="rounded-md border border-[var(--color-gray-300)] px-3 py-1 text-xs font-medium text-[var(--color-gray-700)] bg-white disabled:opacity-60"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      await documentService.updateMetadata(
                                        doc.document_id,
                                        {
                                          hashtags: editingCaseDocHashtags,
                                          classification: editingCaseDocClassification,
                                        }
                                      );
                                      setExistingCaseDocuments((prev) =>
                                        prev.map((d) =>
                                          d.document_id === doc.document_id
                                            ? {
                                                ...d,
                                                classification:
                                                  editingCaseDocClassification,
                                                hashtags: editingCaseDocHashtags,
                                                approval_status:
                                                  editingCaseDocClassification ===
                                                  'Confidential'
                                                    ? 'Pending'
                                                    : d.approval_status,
                                              }
                                            : d
                                        )
                                      );
                                      toast.success('Document metadata updated.');
                                    } catch (err: any) {
                                      toast.error(
                                        err?.response?.data?.message ||
                                          'Failed to update document metadata.'
                                      );
                                    } finally {
                                      setEditingCaseDocumentId(null);
                                    }
                                  }}
                                  disabled={isUploading}
                                  className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-white disabled:opacity-60"
                                >
                                  Save Changes
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}

            {/* File list with bulk tools */}
            {filesWithMeta.length > 0 && (() => {
              const allSelected = filesWithMeta.length > 0 && selectedIds.size === filesWithMeta.length;
              const someSelected = selectedIds.size > 0 && selectedIds.size < filesWithMeta.length;

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>

                  {/* Bulk toolbar - compact and card friendly */}
                  <div
                    className="rounded-2xl border border-[var(--color-gray-200)] bg-[linear-gradient(135deg,#f8fbff_0%,#f7f7f8_100%)] p-4 md:p-5"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 'var(--spacing-4)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 500,
                        color: 'var(--color-gray-700)',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected;
                        }}
                        onChange={toggleSelectAll}
                        disabled={isUploading}
                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                      />
                      <span>
                        {selectedIds.size > 0
                          ? `${selectedIds.size} selected · Apply bulk values`
                          : 'Select / deselect all'}
                      </span>
                    </label>

                    {selectedIds.size > 0 && (
                      <div
                        className="w-full lg:w-auto"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--spacing-3)',
                          flexWrap: 'wrap',
                        }}
                      >
                        {/* Folder/Document Type dropped — classification + hashtags only */}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {(['Public', 'Confidential'] as const).map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              onClick={() =>
                                setBulkValues((prev) => ({ ...prev, classification: opt }))
                              }
                              style={{
                                padding: '8px 12px',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 500,
                                borderRadius: 'var(--radius-md)',
                                border: `1px solid ${
                                  bulkValues.classification === opt
                                    ? opt === 'Confidential'
                                      ? 'var(--color-error)'
                                      : 'var(--color-primary)'
                                    : 'var(--color-gray-200)'
                                }`,
                                background:
                                  bulkValues.classification === opt
                                    ? opt === 'Confidential'
                                      ? '#fef2f2'
                                      : 'var(--color-primary-50)'
                                    : 'white',
                                color:
                                  bulkValues.classification === opt
                                    ? opt === 'Confidential'
                                      ? 'var(--color-error)'
                                      : 'var(--color-primary)'
                                    : 'var(--color-gray-600)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 3,
                                minWidth: 120,
                              }}
                            >
                              {opt === 'Public' ? <Globe size={11} /> : <Lock size={11} />}
                              {opt}
                            </button>
                          ))}
                        </div>

                        <button
                          type="button"
                          onClick={applyBulk}
                          style={{
                            padding: '8px 16px',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--color-primary)',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          Apply
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedIds(new Set())}
                          style={{
                            padding: '8px 12px',
                            fontSize: 'var(--font-size-sm)',
                            borderRadius: 'var(--radius-md)',
                            background: 'transparent',
                            color: 'var(--color-gray-600)',
                            border: '1px solid var(--color-gray-200)',
                            cursor: 'pointer',
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Per-file cards */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--spacing-4)',
                    }}
                  >
                    {filesWithMeta.map((f) => {
                      const isSelected = selectedIds.has(f.id);

                      return (
                        <div
                          key={f.id}
                          className="rounded-2xl border p-4 shadow-[0_10px_30px_rgba(15,23,42,0.04)] transition-all md:p-5"
                          style={{
                            borderColor:
                              f.status === 'error'
                                ? 'var(--color-error)'
                                : isSelected
                                ? 'var(--color-primary)'
                                : 'var(--color-gray-200)',
                            background:
                              f.status === 'success'
                                ? 'var(--color-success-light)'
                                : isSelected
                                ? 'var(--color-primary-50)'
                                : 'white',
                          }}
                        >
                          <div className="flex flex-col gap-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                              <div className="flex min-w-0 items-start gap-3">
                                <div className="flex items-center pt-1">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleSelectRow(f.id)}
                                    disabled={isUploading}
                                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                                  />
                                </div>

                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <div style={{ marginTop: 2 }}>{getFileIcon(f.file)}</div>
                                    <span
                                      title={f.file.name}
                                      className="min-w-0 break-all text-sm font-semibold text-[var(--color-gray-800)] sm:break-normal sm:truncate"
                                    >
                                      {f.file.name}
                                    </span>
                                  </div>
                                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--color-gray-500)]">
                                    <span>{(f.file.size / 1024).toFixed(0)} KB</span>
                                    <span>{f.classification}</span>
                                    <span>{f.hashtags.length}/5 tags</span>
                                  </div>
                                  {f.status === 'uploading' && (
                                    <div
                                      style={{
                                        marginTop: 8,
                                        height: 6,
                                        background: 'var(--color-gray-100)',
                                        borderRadius: 999,
                                        overflow: 'hidden',
                                      }}
                                    >
                                      <div
                                        style={{
                                          height: '100%',
                                          width: `${f.progress}%`,
                                          background: 'var(--color-primary)',
                                          borderRadius: 999,
                                          transition: 'width 0.2s',
                                        }}
                                      />
                                    </div>
                                  )}
                                  {f.status === 'success' && (
                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-success)' }}>
                                      ✓ Uploaded
                                    </span>
                                  )}
                                  {f.status === 'error' && (
                                    <span
                                      title={f.errorMessage}
                                      style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error)' }}
                                    >
                                      ✗ Failed
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center justify-end">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveFile(f.id)}
                                  disabled={isUploading}
                                  className="flex h-10 w-10 items-center justify-center rounded-xl border border-transparent text-[var(--color-gray-400)] transition-all hover:border-red-100 hover:bg-red-50 hover:text-[var(--color-error)] disabled:cursor-not-allowed disabled:opacity-60"
                                  style={{
                                    cursor: isUploading ? 'not-allowed' : 'pointer',
                                  }}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>

                            {/* Folder/Document Type dropped — classification + hashtags only */}
                            <div className="grid gap-4 lg:grid-cols-[minmax(190px,220px)_minmax(0,1fr)] xl:grid-cols-[minmax(180px,210px)_minmax(0,1fr)]">
                                <div>
                                  <label
                                    style={{
                                      display: 'block',
                                      fontSize: 'var(--font-size-xs)',
                                      fontWeight: 600,
                                      color: 'var(--color-gray-600)',
                                      marginBottom: 6,
                                    }}
                                  >
                                    Classification
                                  </label>
                                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1">
                                    {(['Public', 'Confidential'] as const).map((opt) => (
                                      <button
                                        key={opt}
                                        type="button"
                                        disabled={isUploading}
                                        onClick={() =>
                                          updateFileRow(f.id, { classification: opt })
                                        }
                                        style={{
                                          width: '100%',
                                          minHeight: 44,
                                          padding: '8px 10px',
                                          fontSize: 'var(--font-size-xs)',
                                          fontWeight: 600,
                                          borderRadius: 'var(--radius-lg)',
                                          border: `1px solid ${
                                            f.classification === opt
                                              ? opt === 'Confidential'
                                                ? 'var(--color-error)'
                                                : 'var(--color-primary)'
                                              : 'var(--color-gray-200)'
                                          }`,
                                          background:
                                            f.classification === opt
                                              ? opt === 'Confidential'
                                                ? '#fef2f2'
                                                : 'var(--color-primary-50)'
                                              : 'white',
                                          color:
                                            f.classification === opt
                                              ? opt === 'Confidential'
                                                ? 'var(--color-error)'
                                                : 'var(--color-primary)'
                                              : 'var(--color-gray-600)',
                                          cursor: isUploading ? 'not-allowed' : 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: 5,
                                        }}
                                      >
                                        {opt === 'Public' ? <Globe size={12} /> : <Lock size={12} />}
                                        {opt}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                <div className="min-w-0">
                                  <label
                                    style={{
                                      display: 'block',
                                      fontSize: 'var(--font-size-xs)',
                                      fontWeight: 600,
                                      color: 'var(--color-gray-600)',
                                      marginBottom: 6,
                                    }}
                                  >
                                    Hashtags (optional)
                                  </label>
                                  <HashtagInput
                                    value={f.hashtags}
                                    onChange={(tags) => updateFileRow(f.id, { hashtags: tags })}
                                    maxTags={5}
                                    placeholder="Add keywords like case, district, evidence..."
                                  />
                                </div>
                              </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div className="flex items-center justify-end gap-3">
              {isSavingDraft ? (
                <span
                  style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}
                >
                  Saving draft…
                </span>
              ) : lastDraftSavedAt ? (
                <span
                  style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}
                >
                  Draft saved · {lastDraftSavedAt.toLocaleTimeString()}
                </span>
              ) : null}
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSavingDraft || isUploading}
                className="h-10 px-4 rounded-md font-semibold text-sm border border-primary text-primary bg-white disabled:opacity-60 transition-opacity"
              >
                {isSavingDraft ? (currentDraftId ? 'Editing...' : 'Saving...') : currentDraftId ? 'Edit' : 'Save'}
              </button>
              <button
                type="submit"
                disabled={isUploading || (!currentDraftId && filesWithMeta.length === 0)}
                className={`h-10 px-5 bg-primary text-white rounded-md font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-opacity ${
                  currentDraftId ? '' : 'min-w-[260px]'
                }`}
              >
                <Upload size={18} />
                {isUploading
                  ? 'Uploading...'
                  : currentDraftId
                  ? 'Upload Document'
                  : filesWithMeta.length > 0
                  ? `Upload ${filesWithMeta.length} File${filesWithMeta.length > 1 ? 's' : ''}`
                  : 'Upload Document'}
              </button>
            </div>

            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UploadPage;
